import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  DEFAULT_CORNERS,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectEffectiveDate,
  selectCompTypes,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateSFRGLAAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatio: number | undefined,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
) => {
  const compImprovements = improvements[compID];
    const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;

  if (compType === 'arv') {
    subjectImprovements = improvements.subjectArv;
  }

  const subjectPropertyIdentification = propertyIdentification.subject;

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(depreciationRatio) ||
    isUndefined(effectiveDate)
  ) {
    return undefined;
  }

  const {
    gla,
    corners,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = subjectImprovements;
  const { gla: compGla, propertyType: compPropertyType = 'singleFamilyResidence' } =
    compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  if (
    isUndefined(zipCode) ||
    isUndefined(gla) ||
    isUndefined(quality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1 ||
    isUndefined(compGla)
  ) {
    return undefined;
  }

  return adjCalculator.getSFRGLAAdj({
    subject: {
      quality,
      sqft: AloftArea.schema.parse(gla),
      zipCode,
      corners: corners ?? DEFAULT_CORNERS,
      propertyType,
    },
    comp: { sqft: AloftArea.schema.parse(compGla), propertyType: compPropertyType },
    depreciationRatio,
  }).adjustment;
};

const selectSFRGLAAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    (state, id: string) => id,
    selectCompTypes,
  ],
  calculateSFRGLAAdj,
);

export const selectSFRGLAAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    selectCompSequence,
    selectCompTypes,
  ],
  (
    adjustmentProps,
    improvements,
    propertyIdentification,
    depreciationRatio,
    effectiveDate,
    compSequence,
    compTypes
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateSFRGLAAdj(
        adjustmentProps,
        improvements,
        propertyIdentification,
        depreciationRatio,
        effectiveDate,
        propID,
        compTypes,
      );
      if (isDefined(adj)) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedSFRGLAAdj = createPriceSelector(
  [selectSFRGLAAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'mainDwelling', adjSettings);
  },
);

export const selectAppliedSFRGLAAdjDisplay = createSelector([selectAppliedSFRGLAAdj], (adj) => {
  if (isUndefined(adj)) {
    return undefined;
  }

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});
